import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames/dedupe';
import $ from 'jquery';
import React, {useCallback, useRef} from 'react';

import {assertAndContinue} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants, {
  FOLDER_ITEM_TYPES,
  PREVIEWABLE_NOTE_LINKED_OBJECT_TYPES,
} from 'constants/ModelTypeConstants';
import filestates from 'FileStates';
import NoteDropTarget from 'notebook/notes/drag-and-drop/NoteDropTarget';
import NoteConstants from 'notebook/notes/NoteConstants';
import PreviewResizer from 'notebook/notes/previews/PreviewResizer';
import UploadDropdownButton from 'notebook/notes/previews/UploadDropdownButton';
import {
  AttachedExternalFileAddedVia,
  trackAttachedExternalFile,
} from 'notebook/util/analytics/FileAnalyticsUtils';
import {UploadSource} from 'notebook/util/NoteUtilConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import APIUtil from 'util/APIUtil';
import {typeToName} from 'util/NameUtil';
import {InputChangeEvent} from 'util/Types';
import {FILE_TYPES_WITH_MODAL_EDITOR} from 'workflows/WorkflowConstants';

const ALLOWED_LINK_TYPES = NoteConstants.NOTE_PREVIEW_SUPPORTED_TYPES.concat(
  PREVIEWABLE_NOTE_LINKED_OBJECT_TYPES
);
export const ONE_MEGABYTE = 1024 * 1024;
export const MAX_FILE_SIZE_FOR_PREVIEW_BYTES = 100 * ONE_MEGABYTE;

function _stopPropagationForMediocreItemSelect(e: React.MouseEvent): void {
  e.stopPropagation();
}

import {PreviewProps} from './PreviewMixin';

export interface PreviewMixinHookConfig extends PreviewProps {
  className: string;
  noteType: ModelTypeConstants;
  renderItem: (isSelected?: boolean) => JSX.Element | null;

  getItemType?: () => string;
  handleContextMenu?: ((e: React.MouseEvent) => void) | null;
  isImageToolbarOpen?: () => boolean;
  openItem?: (e: React.MouseEvent) => void;
  renderCustomTypeIcon?: () => JSX.Element;
  renderHeaderIcons?: () => JSX.Element;
  renderImageToolbar?: (isSelected?: boolean) => JSX.Element;
  renderName?: () => JSX.Element;
  renderNameExtra?: (isSelected?: boolean) => JSX.Element;
  renderOpenExtra?: () => JSX.Element;
  task?: string;
}

interface PreviewMixinHook {
  itemURL: () => string;
  renderOpen: () => Array<JSX.Element> | null;
  renderTypeIcon: () => JSX.Element | undefined;
  fileChangeHandler: (e: InputChangeEvent) => void;
  renderSettings: () => JSX.Element | null;
  render: () => JSX.Element;
}

/**
 * A hook which returns a `render` method that can be used to easily create a Preview within a Notebook.
 *
 * This hook allows for quick creation of a component that can "preview" a file within the Notebook. It takes
 * in a few required properties like:
 * - className -- the CSS that the previewed component should be wrapped with
 * - noteType -- the ModelTypeConstant that this Preview class should be used for
 * - renderItem -- a Component that we'll use to render the item within the Notebook
 *
 * Using these, you can quickly create a new Preview for some type like the following:
 *
 *   function MyPreview(props: PreviewProps): JSX.Element {
 *     const {render} = usePreviewMixing({
 *       ...props,
 *       className: 'note-MyPreview',
 *       noteType: ModelTypeConstants.MY_ITEM_TYPE,
 *       renderItem: () => {
 *         <div>{props.item.myattribute}
 *       }
 *     })
 *     return render();
 *   }
 */
export default function usePreviewMixin(config: PreviewMixinHookConfig): PreviewMixinHook {
  const {
    className,
    fileId,
    getItemType,
    handleContextMenu,
    isImageToolbarOpen,
    isSelected,
    item,
    noteType,
    openItem,
    placeholder,
    renderCustomTypeIcon,
    renderHeaderIcons,
    renderImageToolbar,
    renderItem,
    renderName,
    renderNameExtra,
    renderOpenExtra,
    notePartCid,
    uploadFile,
    ...props
  } = config;

  const fileInput = useRef<HTMLInputElement>(null);
  // allow opening / show link next to name
  const showOpenLink = props.showOpenLink !== undefined ? props.showOpenLink : true;
  // Open file in new tab on link click / image double click
  const followLink = props.followLink !== undefined ? props.followLink : false;

  const canOpen = useCallback(
    (): boolean =>
      showOpenLink &&
      Array.from(ALLOWED_LINK_TYPES).includes(noteType) &&
      !placeholder &&
      !FILE_TYPES_WITH_MODAL_EDITOR.includes(APIUtil.apiIdToType(fileId)),
    [showOpenLink, noteType, placeholder, fileId]
  );

  const _triggerFileInput = useCallback(() => {
    $(fileInput.current!).click();
  }, [fileInput]);

  const defaultRenderName = useCallback(
    (): JSX.Element => (
      <div
        className="note-itemName"
        {...getTestProps({component: 'PreviewMixin', element: 'previewItemName'})}
      >
        {typeof renderName === 'function'
          ? renderName()
          : // @ts-expect-error(2339): NoteLinkedPrimerPair uses renderName as it doesn't have an item.name
            item.name}
        {typeof renderNameExtra === 'function' ? renderNameExtra(isSelected) : undefined}
      </div>
    ),
    [renderName, renderNameExtra, item, isSelected]
  );

  const defaultItemType = useCallback((): string => (getItemType ? getItemType() : 'file'), [getItemType]);

  const itemURL = useCallback((): string => {
    if (Array.from(FOLDER_ITEM_TYPES).includes(noteType)) {
      // @ts-expect-error(2339): not all NotePartItems have an editURL
      return item.editURL;
    } else {
      // @ts-expect-error(2339): not all NotePartItems have an url
      return item.url;
    }
  }, [noteType, item]);

  const defaultOpenItem = useCallback(
    (e: React.MouseEvent): void => {
      _stopPropagationForMediocreItemSelect(e);
      e.preventDefault();
      if (!canOpen()) {
        return;
      }
      if (followLink) {
        window.open(itemURL(), '_blank');
      } else if (openItem != null) {
        openItem(e);
      } else if (item?.id != null) {
        // @ts-expect-error(2345): item.id should be a string, not a string | number
        filestates.get().openFolderItem(item.id, {open: true, source: 'note-preview'});
      } else {
        assertAndContinue(false, 'Was not able to open a Preview', {extra: item});
      }
    },
    [canOpen, followLink, itemURL, openItem, item]
  );

  const renderOpen = useCallback((): Array<JSX.Element> | null => {
    const fileSize = (item as unknown as {externalFile?: {fileSize?: number}}).externalFile?.fileSize ?? 0; // Default to 0 if undefined
    const isFileSizeValid = fileSize <= MAX_FILE_SIZE_FOR_PREVIEW_BYTES;
    const tooltipText = isFileSizeValid
      ? `Open ${defaultItemType()}`
      : `File too large to open ${defaultItemType()}`;
    if (canOpen()) {
      return [
        <Tooltip key="open" text={tooltipText}>
          <Button
            key="open"
            className="previewCTA hidden-for-print"
            buttonStyle={ButtonStyle.TERTIARY_BLUE}
            size={ButtonSize.MINI}
            icon="arrow-up-right-and-arrow-down-left-from-center"
            onClick={defaultOpenItem}
            disabled={!isFileSizeValid}
          />
        </Tooltip>,
        ...(typeof renderOpenExtra === 'function' ? [renderOpenExtra()] : []),
      ];
    } else {
      return null;
    }
  }, [canOpen, item, defaultOpenItem, defaultItemType, renderOpenExtra]);

  const renderTypeIcon = useCallback((): JSX.Element | undefined => {
    if (typeof renderCustomTypeIcon === 'function') {
      return renderCustomTypeIcon();
    }
    if (noteType != null) {
      return (
        <BenchlingIcon
          // @ts-expect-error(2339): not all NotePartItems have a tagSchema
          icon={item?.tagSchema?.iconId ?? noteType}
          className="note-icon u-small-spacing-right pull-left"
        />
      );
    }
    return undefined;
  }, [renderCustomTypeIcon, noteType, item]);

  const fileChangeHandler = useCallback(
    (e: InputChangeEvent): void => {
      const file = e.target.files![0];
      if (file == null) {
        return;
      }
      uploadFile(file, UploadSource.LOCAL_FILE);
      trackAttachedExternalFile(
        fileId,
        AttachedExternalFileAddedVia.InsertAttachment,
        file.type,
        file.size,
        file.name,
        UploadSource.LOCAL_FILE
      );
    },
    [uploadFile, fileId]
  );

  const handleGoogleDriveFiles = useCallback(
    (files: Array<File>) => {
      if (files.length > 0) {
        uploadFile(files[0], UploadSource.GOOGLE_DRIVE);
        trackAttachedExternalFile(
          fileId,
          AttachedExternalFileAddedVia.InsertAttachment,
          files[0].type,
          files[0].size,
          files[0].name,
          UploadSource.GOOGLE_DRIVE
        );
      }
    },
    [uploadFile, fileId]
  );

  const renderSettings = useCallback((): JSX.Element | null => {
    if (!handleContextMenu) {
      return null;
    }
    return (
      <Tooltip key="more" text="More...">
        <Button
          className="previewCTA hidden-for-print"
          buttonStyle={ButtonStyle.SECONDARY_WHITE}
          size={ButtonSize.MINI}
          onClick={handleContextMenu}
          icon="ellipsis"
        />
      </Tooltip>
    );
  }, [handleContextMenu]);

  const render = useCallback(
    (): JSX.Element => (
      <NoteDropTarget
        multiple={false}
        fileId={fileId}
        locked={props.locked}
        disableDropMessage={props.disableDropMessage}
        handleDropExternalFiles={props.handleDropExternalFiles}
        handleDropBenchlingFile={props.handleDropBenchlingFile}
        hasExistingItem={Boolean(item)}
      >
        {/* When a preview is first created, it may not have a setItemWidth function (if it was attached with
        the modal, for instance). The resizer is not rendered until this is passed in. */}
        <div
          className={classNames(className, 'mediocre-itemSelect', 'note-item', {
            'note-item--placeholder': placeholder && props.task == null,
          })}
          onContextMenu={(e) => {
            if (typeof handleContextMenu === 'function') {
              handleContextMenu(e);
            }
          }}
          {...getTestProps({
            component: 'NoteDropTarget',
            element: 'noteLinkedFilePreview',
            ...(notePartCid ? {key: notePartCid} : {}),
          })}
        >
          {item != null ? (
            <div>
              {props.setItemWidth != null && !props.locked ? (
                <PreviewResizer
                  alignment={props.alignment}
                  maxWidth={props.editorWidth}
                  setItemWidth={props.setItemWidth}
                  finalizeItemWidth={props.finalizeItemWidth}
                />
              ) : undefined}
              <h5 className={css(styles.container)}>
                <>
                  {typeof isImageToolbarOpen === 'function' && isImageToolbarOpen() ? (
                    <div className={css(styles.previewToolbarContainerEdit)}>
                      {typeof renderImageToolbar === 'function' ? renderImageToolbar(isSelected) : undefined}
                    </div>
                  ) : (
                    <div className={css(styles.previewToolbarContainer)}>
                      <div className={css(styles.previewNameContainer)}>
                        {renderTypeIcon()}
                        {defaultRenderName()}
                      </div>
                      <div className={css(styles.item)}>{renderHeaderIcons && renderHeaderIcons()}</div>
                      <div className={css(styles.item)}>{renderOpen()}</div>
                      <div className={css(styles.item)}>{renderSettings()}</div>
                    </div>
                  )}
                </>
              </h5>
              {renderItem(isSelected)}
            </div>
          ) : placeholder ? (
            <div className="notePlaceholderItem">
              {props.task != null ? (
                <h5 className="u-no-spacing-top u-no-spacing-bottom">
                  {renderTypeIcon()}
                  <div className="pull-right">
                    <span
                      className="notePlaceholderItem-spinner spinner"
                      {...getTestProps({component: 'PreviewMixin', element: 'loadingIndicator'})}
                    />
                  </div>
                  {defaultRenderName()}
                </h5>
              ) : (
                <div className="notePlaceholderItem-contentWrapper text-center">
                  <div className="notePlaceholderItem-text u-small-spacing-bottom">
                    Drag and drop a file from the sidebar to attach or
                  </div>
                  <input
                    key="hiddenFileInput"
                    type="file"
                    multiple={false}
                    className="hidden"
                    ref={fileInput}
                    onChange={fileChangeHandler}
                    disabled={props.locked}
                    {...getTestProps({component: 'PreviewMixin', element: 'hiddenFileInput'})}
                  />
                  <div className="notePlaceholderItem-buttonWrapper">
                    {BenchlingConfig.ELN_ENABLE_GOOGLE_DRIVE_FILE_PICKER ? (
                      <UploadDropdownButton
                        onLocalUploadClick={_triggerFileInput}
                        onGoogleDriveFilesReady={handleGoogleDriveFiles}
                        fileId={fileId}
                        multiselect={false}
                        disabled={props.locked}
                      />
                    ) : (
                      <Button
                        buttonStyle={ButtonStyle.SECONDARY}
                        size={ButtonSize.SMALL}
                        fullWidth={true}
                        key="uploadFileButton"
                        className="u-tiny-spacing-bottom"
                        onClick={_triggerFileInput}
                        disabled={props.locked}
                        text="Upload a File"
                        dataTestAttrs={{component: 'PreviewMixin', element: 'uploadButton'}}
                      />
                    )}
                    {APIUtil.apiIdToType(fileId) === ModelTypeConstants.SEQUENCE ? (
                      <Button
                        buttonStyle={ButtonStyle.TERTIARY_GRAY}
                        size={ButtonSize.SMALL}
                        fullWidth={true}
                        onClick={props.openSequenceFeatureModal}
                        disabled={props.locked}
                        text="Select A Feature"
                      />
                    ) : undefined}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <h5 className="u-no-spacing-top u-text-uppercase faded u-no-spacing-bottom">
                {renderTypeIcon()}
                (Deleted)
              </h5>
              <div className="note-preview-body">
                {`The ${typeToName(noteType)} corresponding to this note no longer exists.`}
              </div>
            </div>
          )}
        </div>
      </NoteDropTarget>
    ),
    [
      _triggerFileInput,
      className,
      defaultRenderName,
      fileChangeHandler,
      fileId,
      fileInput,
      handleContextMenu,
      handleGoogleDriveFiles,
      isImageToolbarOpen,
      isSelected,
      item,
      noteType,
      notePartCid,
      placeholder,
      props.alignment,
      props.disableDropMessage,
      props.editorWidth,
      props.finalizeItemWidth,
      props.handleDropBenchlingFile,
      props.handleDropExternalFiles,
      props.locked,
      props.openSequenceFeatureModal,
      props.setItemWidth,
      props.task,
      renderHeaderIcons,
      renderImageToolbar,
      renderItem,
      renderOpen,
      renderSettings,
      renderTypeIcon,
    ]
  );

  return {
    itemURL,
    renderOpen,
    renderTypeIcon,
    fileChangeHandler,
    renderSettings,
    render,
  };
}

export const styles = StyleSheet.create({
  container: {
    marginTop: 0,
    marginBottom: 0,
    width: '100%',
    overflowX: 'auto',
  },
  item: {
    float: 'right',
    alignContent: 'center',
  },
  previewToolbarContainerEdit: {
    height: '32px',
    alignContent: 'center',
  },
  previewToolbarContainer: {
    height: '32px',
    alignContent: 'center',
    display: 'flex',
  },
  previewNameContainer: {
    height: '32px',
    alignContent: 'center',
    float: 'left',
    flex: 1,
  },
});
